import EditIcon from "@mui/icons-material/Edit";
import {
    Avatar,
    Chip,
    IconButton,
    Rating,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import moment from "moment";
import React from "react";
import { useSelector } from "react-redux";
import appcolors from "../../constants/Colors";
import { CURRENCY, DEFAULT_VAT_PERCENTAGE, PACKAGE_USAGE_TYPE } from "../../constants/Variables";
import {
    calculateVat,
    roundTo2Decimal
} from "../../utils";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";
import ActionsMenu from "./ActionsMenu";

const tableStyle = {
    "&.MuiTableContainer-root .MuiTableHead-root": {
        backgroundColor: appcolors.primary,
    },
    "&.MuiTableContainer-root .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root":
    {
        padding: "6px 16px",
        color: appcolors.white,
        fontSize: "1rem",
    },
    "& .view_btn": {
        border: "1px solid grey",
        height: 35,
        width: 75,
        fontSize: "0.875rem",
        borderRadius: "6px",
    },
};

export default function PackageTable({
    minWidth,
    tableHeader,
    tableData,
    displayRows,
    showEdit,
    handleEdit,
    handleChangeStatus,
    paginatedData,
    onPageChange,
    onRowsPerPageChange,
    handleUpdateVerification,
    showActions,
    showDelete,
    handleDelete,
    handleUpdatePromotedStatus,
}) {
    const { t } = useTranslation("products");
    const { currentLanguage } = useSelector((state) => state.languageReducer);
    const settings = useSelector((state) => state.settingsReducer.settings);

    return (
        <>
            <TableContainer sx={{ ...tableStyle, overflowX: "auto" }}>
                <Table
                    sx={{
                        minWidth: minWidth ? minWidth : "900px",
                    }}
                >
                    <TableHead>
                        <TableRow>
                            {tableHeader?.map((header) => (
                                <TableCell key={header}>{t("table." + header)}</TableCell>
                            ))}

                            {showActions && (
                                <TableCell sx={{
                                    position: "sticky",
                                    right: 0,
                                    zIndex: 3,
                                    px: 0,
                                    backgroundColor: "primary.main",
                                }}>
                                    {t("table.actions")}
                                </TableCell>
                            )}

                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {


                            tableData?.map((row, index) => {


                                // This is calculated for the base packages
                                const finalPackagePrice =
                                    row.effectivePackagePrice


                                // This is calculated for the base packages
                                const packageVat = calculateVat(
                                    finalPackagePrice,
                                    settings?.tax || DEFAULT_VAT_PERCENTAGE
                                );

                                // This is calculated for the base packages
                                const finalPriceAfterVat = roundTo2Decimal(
                                    finalPackagePrice + packageVat
                                );

                                return (
                                    <TableRow key={index}>
                                        {displayRows.map((val) => (
                                            <React.Fragment key={val}>
                                                {val === "created_date" ? (
                                                    <TableCell>
                                                        {moment(row.createdAt).format("DD/MM/YYYY")}
                                                        <br />
                                                        {moment(row.createdAt).format("hh:mm:ss a")}
                                                    </TableCell>
                                                ) : val === "custom_package_created_by" ? (
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {row?.user?.firstname + " " + row?.user?.lastname}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={400}>
                                                            {row?.user?.email}
                                                        </Typography>
                                                    </TableCell>
                                                ) : val === "store_name" ? (
                                                    <TableCell>
                                                        {row?.createdBy?.businessName || "-"}
                                                    </TableCell>
                                                ) : val === "name_image" ? (
                                                    <TableCell>
                                                        <Stack
                                                            direction={"row"}
                                                            alignItems={"center"}
                                                            gap={1}
                                                            width={"400px"}
                                                        >
                                                            <Avatar
                                                                src={row.coverImage}
                                                                sx={{
                                                                    height: 55,
                                                                    width: 55,
                                                                    borderRadius: "6px",
                                                                    backgroundColor: "#edebeb",
                                                                    border: "1px solid #edebeb",
                                                                }}
                                                            />
                                                            <Tooltip title={row.name?.[currentLanguage] || ""} placement="top">
                                                                <Typography
                                                                    variant={"body1"}
                                                                    textTransform={"capitalize"}
                                                                    sx={{
                                                                        display: "-webkit-box",
                                                                        WebkitLineClamp: 2,
                                                                        WebkitBoxOrient: "vertical",
                                                                        overflow: "hidden",
                                                                        maxWidth: "100%",
                                                                    }}
                                                                >
                                                                    {row.name[currentLanguage]}
                                                                </Typography>
                                                            </Tooltip>
                                                        </Stack>
                                                    </TableCell>
                                                ) : val === "solution_type" ? (
                                                    <TableCell sx={{ textTransform: "capitalize" }}>
                                                        {PACKAGE_USAGE_TYPE.find(item => row?.solutionType?.includes(item.value))?.label?.[currentLanguage] || <Typography variant="body2" color="info.main" >{t("table.notAddedYet")}</Typography>}
                                                    </TableCell>
                                                ) : val === "category_name" ? (
                                                    <TableCell sx={{ textTransform: "capitalize" }}>
                                                        {row?.category?.name?.[currentLanguage]}
                                                    </TableCell>
                                                ) : val === "sub_category_name" ? (
                                                    <TableCell sx={{ textTransform: "capitalize" }}>
                                                        {row?.subCategory?.length > 0 ? row?.subCategory.map((x, idx) => (
                                                            <Typography key={idx} variant="body2" mb={0.5} component="span">
                                                                {x?.name?.[currentLanguage]}
                                                                {idx !== row.subCategory.length - 1 ? ", " : ""}
                                                            </Typography>
                                                        )) : <Typography variant="body2" color="info.main" >{t("table.notAddedYet")}</Typography>}
                                                    </TableCell>
                                                ) : val === "sub_sub_category_name" ? (
                                                    <TableCell sx={{ textTransform: "capitalize" }}>
                                                        {row?.subSubCategory?.length > 0 ? row?.subSubCategory.map((x, idx) => (
                                                            <Typography key={idx} variant="body2" mb={0.5} component="span">
                                                                {x?.name?.[currentLanguage]}
                                                                {idx !== row.subSubCategory.length - 1 ? ", " : ""}
                                                            </Typography>
                                                        )) : <Typography variant="body2" color="info.main" >{t("table.notAddedYet")}</Typography>}
                                                    </TableCell>
                                                ) : val === "package_price" ? (
                                                    <TableCell sx={{ textTransform: "capitalize" }}>
                                                        {`${roundTo2Decimal(row.packagePrice)} ${CURRENCY}`}
                                                    </TableCell>
                                                ) : val === "package_effective_price" ? (
                                                    <TableCell sx={{ textTransform: "capitalize" }}>
                                                        {`${roundTo2Decimal(row.effectivePackagePrice)} ${CURRENCY}`}
                                                    </TableCell>
                                                ) : val === "app_vat" ? (
                                                    <TableCell>
                                                        {settings?.tax || DEFAULT_VAT_PERCENTAGE}%
                                                    </TableCell>
                                                ) : val === "package_price_after_vat" ? (
                                                    <TableCell>{`${finalPriceAfterVat} ${CURRENCY}`}</TableCell>
                                                ) : val === "package_vat_amount" ? (
                                                    <TableCell>{`${packageVat} ${CURRENCY}`}</TableCell>
                                                ) : val === "rating_in_number" ? (
                                                    <TableCell>
                                                        <Stack direction={"row"} alignItems={"center"} justifyContent={"center"}>
                                                            <Rating
                                                                value={row.rating}
                                                                precision={0.5}
                                                                readOnly
                                                                size="small"

                                                            />

                                                            <Typography variant="body2">
                                                                ({row.rating?.toFixed(0) || 0})
                                                            </Typography>
                                                        </Stack>
                                                    </TableCell>
                                                ) : val === "status" ? (
                                                    <TableCell>
                                                        <Switch
                                                            checked={row.status}
                                                            onChange={(e) =>
                                                                handleChangeStatus(e.target.checked, row)
                                                            }
                                                            inputProps={{ "aria-label": "controlled" }}
                                                        />
                                                    </TableCell>
                                                ) : val === "verified" ? (
                                                    <TableCell>
                                                        <Switch
                                                            checked={row?.isVerified}
                                                            onChange={(e) =>
                                                                handleUpdateVerification(
                                                                    e.target.checked,
                                                                    row
                                                                )
                                                            }
                                                        />
                                                    </TableCell>
                                                ) : val === "package_out_of_stock" ? (
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={row.outOfStock ? t("table.outOfStock") : t("table.inStock")}
                                                            color={row.outOfStock ? "error" : "success"}
                                                            sx={{
                                                                fontWeight: 600,
                                                                textTransform: "capitalize",
                                                                "& .MuiChip-label": { px: 1.25 },
                                                            }}
                                                        />
                                                    </TableCell>
                                                ) : val === "package_avaibliblity" ? (
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={row.isAvailable ? t("table.available") : t("table.notAvailable")}
                                                            color={row.isAvailable ? "success" : "default"}
                                                            variant={row.isAvailable ? "filled" : "outlined"}
                                                            sx={{
                                                                fontWeight: 600,
                                                                textTransform: "capitalize",
                                                                "& .MuiChip-label": { px: 1.25 },
                                                                ...(!row.isAvailable && { borderColor: "grey.400", color: "text.secondary" }),
                                                            }}
                                                        />
                                                    </TableCell>
                                                ) : val === "order_allowed" ? (
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={row.isAllowedOrder ? t("table.allowed") : t("table.notAllowed")}
                                                            color={row.isAllowedOrder ? "success" : "default"}
                                                            variant={row.isAllowedOrder ? "filled" : "outlined"}
                                                            sx={{
                                                                fontWeight: 600,
                                                                textTransform: "capitalize",
                                                                "& .MuiChip-label": { px: 1.25 },
                                                                ...(!row.isAllowedOrder && { borderColor: "grey.400", color: "text.secondary" }),
                                                            }}
                                                        />
                                                    </TableCell>
                                                ) : val === "menu_actions" ? (
                                                    <TableCell
                                                        sx={{
                                                            position: "sticky",
                                                            right: 0,
                                                            backgroundColor: "secondary.light",
                                                            zIndex: 3,
                                                            minWidth: 50,
                                                            boxShadow: "inset 4px 0 8px rgba(0,0,0,0.08)",
                                                        }}
                                                    >
                                                        <ActionsMenu
                                                            row={row}
                                                            showEdit={showEdit}
                                                            showDelete={showDelete}
                                                            showProductStatus={true}
                                                            showVerifiedStatus={true}
                                                            showPromotedStatus={true}
                                                            handleEdit={handleEdit}
                                                            handleDelete={handleDelete}
                                                            handleChangeProductStatus={handleChangeStatus}
                                                            handleChangeVerifiedStatus={handleUpdateVerification}
                                                            handleChangePromotedStatus={handleUpdatePromotedStatus}
                                                        />
                                                    </TableCell>
                                                ) : val === "actions" ? (
                                                    <TableCell
                                                        sx={{
                                                            position: "sticky",
                                                            right: 0,
                                                            backgroundColor: "secondary.light",
                                                            zIndex: 2,
                                                            minWidth: 50,
                                                            boxShadow: "inset 4px 0 8px rgba(0,0,0,0.08)",

                                                        }}
                                                    >
                                                        <Stack direction={"row"} alignItems={"center"} justifyContent={"center"}>
                                                            {showEdit && (
                                                                <IconButton onClick={() => handleEdit(row)}>
                                                                    <EditIcon
                                                                        sx={{ fontSize: 18, color: "primary.main" }}
                                                                    />
                                                                </IconButton>
                                                            )}
                                                            {showDelete && (
                                                                <IconButton onClick={() => handleDelete(row)}>
                                                                    <DeleteIcon
                                                                        sx={{ fontSize: 18, color: "error.main" }}
                                                                    />
                                                                </IconButton>
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                ) : (
                                                    <TableCell>
                                                        <Typography
                                                            lineHeight="normal"
                                                            fontSize={"0.875rem"}
                                                        >
                                                            {row[val]}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </TableRow>
                                );
                            })
                        }
                    </TableBody>
                </Table>
            </TableContainer>



            <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={paginatedData.total}
                rowsPerPage={paginatedData.limit}
                page={paginatedData.page - 1}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />

        </>
    );
}
